/* eslint-disable max-lines */
import React, { forwardRef, useState, useMemo, useEffect } from 'react'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import InputLabel from '@mui/material/InputLabel'

import ArrowDownIcon from '../icons/ArrowDownIcon'
import { useNative } from '../../hooks'
import {
  getSelectValueKey,
  handleSelectChange,
  optionsToMap,
} from './Select.helper'

const Select = forwardRef((props, ref) => {
  const {
    SelectProps: SelectPartialProps,
    options = [],
    filterOptions = options,
    mapping = { label: 'label', value: 'value' },
    native: isNative,
    checkable,
    multiple,
    value,
    placeholder,
    onChange = () => '',
    label,
    ...other
  } = props

  const { label: labelKey, value: valueKey } = mapping

  const optionsMap = useMemo(() => {
    return optionsToMap(options, valueKey)
  }, [options, valueKey])

  const { native } = useNative({ isNative })

  const selectValKey = getSelectValueKey({
    valueKey,
    value,
    multiple,
  })

  const renderValue = (selected) => {
    if (SelectPartialProps?.renderValue) {
      return SelectPartialProps?.renderValue(selected)
    }

    if (!selected?.length && placeholder) {
      return <InputLabel>{placeholder}</InputLabel>
    }

    if (!multiple) {
      return optionsMap[selected]?.[labelKey] || optionsMap[selected]
    }

    return selected.map((s) => optionsMap[s]?.[labelKey] || s).join(', ')
  }

  const [selectedValue, setSelectedValue] = useState(selectValKey)

  const inputLabel = !placeholder ? label : selectedValue?.length && label

  const handleChange = (event) => {
    const res = handleSelectChange({ event, native, multiple, optionsMap })

    onChange(res.selectedOption)
    setSelectedValue(res.selectedVal)
  }

  useEffect(() => {
    const newSelectValKey = getSelectValueKey({
      valueKey,
      value,
      multiple,
    })

    setSelectedValue(newSelectValKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <TextField
      ref={ref}
      {...other}
      select
      SelectProps={{
        ...SelectPartialProps,
        native,
        multiple,
        renderValue,
        displayEmpty: !native,
        IconComponent: (p) => (
          <ArrowDownIcon
            {...p}
            sx={{ fontSize: '8px !important', mr: '3px' }}
          />
        ),
      }}
      onChange={handleChange}
      value={selectedValue}
      label={inputLabel || undefined}
    >
      {placeholder && !native && (
        <MenuItem disabled value="">
          {placeholder}
        </MenuItem>
      )}
      {placeholder && native && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {filterOptions.map((option) =>
        native ? (
          <option
            value={option[valueKey] ?? option}
            key={option[valueKey] || option}
          >
            {option[labelKey] ?? option}
          </option>
        ) : (
          <MenuItem
            value={option[valueKey] ?? option}
            key={option[valueKey] || option}
          >
            {checkable && multiple && (
              <Checkbox
                checked={selectedValue.indexOf(option[valueKey] ?? option) > -1}
              />
            )}
            {option[labelKey] ?? option}
          </MenuItem>
        )
      )}
    </TextField>
  )
})

export default Select
